import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

import { getGlobalConfig } from "../../../services/backend/AppConfigService";
import {
  CANCEL_NOTICE_PLACEHOLDERS,
  CANCEL_NOTICE_TEXT_FIELDS,
  DEFAULT_CANCEL_NOTICE_TEXTS,
  DEFAULT_PQRSD_CANCEL_NOTICE_TEXTS,
  buildCancelNoticeEmail,
  cancelNoticeBaseUrl,
} from "../../../services/backend/gfunctions/handleCorrespondence/cancelNoticeEmail";

import { pqrsdPublicPath, pqrsdText } from "../../pqrsd/pqrsdLabel";
// The two emails an annulment sends, each with its own editable texts.
const EMAILS = {
  radicado: {
    field: "texts",
    defaults: DEFAULT_CANCEL_NOTICE_TEXTS,
    label: "Correo del radicado",
    help: "Lo reciben los destinatarios, las copias y, si está activado, los remitentes.",
    switchKeys: ["recipients", "carbonCopy", "senders"],
  },
  pqrsd: {
    field: "pqrsdTexts",
    defaults: DEFAULT_PQRSD_CANCEL_NOTICE_TEXTS,
    label: "Correo al ciudadano (PQRSD)",
    help: "Lo recibe quien presentó la PQRSD, con el enlace a su seguimiento.",
    switchKeys: ["pqrsdCitizen"],
  },
};

// Who the radicado email is previewed as: the greeting and the {relacion}
// wording change with it.
const RADICADO_AUDIENCES = [
  { value: "recipients", label: "Destinatario", switchKey: "recipients" },
  { value: "carbonCopy", label: "Copia", switchKey: "carbonCopy" },
  { value: "senders", label: "Remitente", switchKey: "senders" },
];

// The editable texts, grouped the way they appear in the email, with where
// each one shows up.
const FIELD_GROUPS = [
  {
    title: "Asunto y título",
    fields: {
      subject: "Lo que se ve en la bandeja de entrada.",
      title: "El encabezado grande, bajo el logo.",
    },
  },
  {
    title: "Mensaje",
    fields: {
      intro: "El primer párrafo, justo después del saludo.",
      button: "El botón que abre el radicado o el seguimiento de la PQRSD.",
    },
  },
  {
    title: "Cierre",
    fields: {
      closing: "La nota pequeña bajo el botón.",
      contact: "La línea de contacto. Los correos que escribas se convierten en enlaces.",
      footer: "El texto gris al pie del correo.",
    },
  },
];

const FIELD_BY_KEY = Object.fromEntries(CANCEL_NOTICE_TEXT_FIELDS.map((f) => [f.key, f]));

// A made-up radicado and PQRSD for the preview. The email is built by the same
// module the cloud function sends with, so only these values differ.
const PREVIEW_FILED = {
  filedId: "CI_20260914-00042",
  referenceId: "preview",
  subject: "Solicitud de certificado laboral",
  doneAt: new Date(Date.now() - 3 * 864e5),
};
const PREVIEW_PQRSD = {
  filedId: "PQR_20260914-00042",
  filedType: "pqrsd",
  subject: "Ruido en la vía pública",
  doneAt: new Date(Date.now() - 3 * 864e5),
};
const PREVIEW_PEOPLE = {
  citizen: { name: "Laura", lastName: "Martínez" },
  recipients: { name: "Ana María", lastName: "Gómez", usertitle: "Doctora" },
  carbonCopy: null,
  senders: { name: "Carlos", lastName: "Restrepo", usertitle: "Señor" },
};
const PREVIEW_REASON = "El documento se radicó por error: correspondía a otra dependencia.";

export default function CancelEmailEditor({ notices, setNotices }) {
  const [email, setEmail] = useState("radicado");
  const [radicadoAudience, setRadicadoAudience] = useState("recipients");
  // The portal URL links come from (undefined while loading) and the brand.
  const [baseUrl, setBaseUrl] = useState(undefined);
  const [brand, setBrand] = useState({});
  // Where a placeholder button inserts: the last text field that had focus,
  // at its caret.
  const inputs = useRef({});
  const [focused, setFocused] = useState("intro");

  useEffect(() => {
    let alive = true;
    getGlobalConfig().then((global) => {
      if (!alive) return;
      setBaseUrl(cancelNoticeBaseUrl(global?.publicBaseUrl));
      setBrand({
        color: global?.brandColor,
        name: global?.brandName,
        orgName: global?.orgName,
        logo: global?.logoUrl,
        logoWidth: global?.logoWidth,
      });
    });
    return () => {
      alive = false;
    };
  }, []);

  const current = EMAILS[email];
  const texts = notices[current.field] || current.defaults;
  const audience = email === "pqrsd" ? "citizen" : radicadoAudience;

  const setText = (key, value) =>
    setNotices((prev) => ({
      ...prev,
      [current.field]: { ...(prev[current.field] || current.defaults), [key]: value },
    }));

  const restoreAll = () =>
    setNotices((prev) => ({ ...prev, [current.field]: { ...current.defaults } }));

  const allDefault = CANCEL_NOTICE_TEXT_FIELDS.every(({ key }) => (texts[key] ?? "") === current.defaults[key]);

  const insertPlaceholder = (placeholderKey) => {
    const key = focused || "intro";
    const { max } = FIELD_BY_KEY[key];
    const token = `{${placeholderKey}}`;
    const value = texts[key] ?? "";
    const el = inputs.current[`${email}:${key}`];
    const start = el && typeof el.selectionStart === "number" ? el.selectionStart : value.length;
    const end = el && typeof el.selectionEnd === "number" ? el.selectionEnd : value.length;
    const next = `${value.slice(0, start)}${token}${value.slice(end)}`.slice(0, max);
    setText(key, next);
    // Put the caret right after what was inserted, once React has re-rendered.
    requestAnimationFrame(() => {
      const input = inputs.current[`${email}:${key}`];
      if (!input) return;
      input.focus();
      const caret = Math.min(start + token.length, next.length);
      input.setSelectionRange(caret, caret);
    });
  };

  const { subject, html } = useMemo(
    () =>
      buildCancelNoticeEmail({
        filed: audience === "citizen" ? PREVIEW_PQRSD : PREVIEW_FILED,
        cancellation: { reason: PREVIEW_REASON, cancelledAt: new Date() },
        audience,
        person: PREVIEW_PEOPLE[audience],
        filedUrl: audience === "citizen"
          ? `${baseUrl || window.location.origin}${pqrsdPublicPath("estado")}?t=…`
          : `${baseUrl || window.location.origin}/filed/view/${PREVIEW_FILED.referenceId}`,
        includeReason: notices.includeReason,
        brand,
        texts: notices.texts,
        pqrsdTexts: notices.pqrsdTexts,
      }),
    [audience, notices.includeReason, notices.texts, notices.pqrsdTexts, baseUrl, brand]
  );

  // Whether the email being edited would actually go out with the current switches.
  const notSentReason = !notices.enabled
    ? "La notificación está desactivada en la pestaña Anulación: este correo no se enviará."
    : email === "pqrsd" && !notices.pqrsdCitizen
      ? pqrsdText("El envío a ciudadanos de PQRSD está desactivado en la pestaña Anulación.")
      : email === "radicado" && !notices[RADICADO_AUDIENCES.find((a) => a.value === radicadoAudience).switchKey]
        ? `El envío a ${RADICADO_AUDIENCES.find((a) => a.value === radicadoAudience).label.toLowerCase()}s está desactivado en la pestaña Anulación.`
        : null;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <ToggleButtonGroup
          exclusive
          size="small"
          color="primary"
          value={email}
          onChange={(_, next) => next && setEmail(next)}
          sx={{ flexWrap: "wrap" }}
        >
          {Object.entries(EMAILS).map(([key, e]) => (
            <ToggleButton key={key} value={key} sx={{ textTransform: "none", px: 2 }}>
              {pqrsdText(e.label)}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Button
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={restoreAll}
          disabled={allDefault}
          sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
        >
          Restaurar todos los textos
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {pqrsdText(current.help)} El saludo, la tabla con los datos del radicado y el recuadro del motivo se
        generan solos.
      </Typography>

      <Grid container spacing={3}>
        {/* Editor */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Insertar dato del radicado
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Se agrega donde está el cursor
              {FIELD_BY_KEY[focused] ? <> en <strong>{FIELD_BY_KEY[focused].label}</strong></> : null}, y en
              cada correo se reemplaza por el valor real.
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {CANCEL_NOTICE_PLACEHOLDERS.map((p) => (
                <Tooltip key={p.key} title={`${p.help} Se escribe {${p.key}}.`}>
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    icon={<AddIcon />}
                    label={p.label}
                    // Keep the focus (and caret) in the field being edited.
                    onMouseDown={(evt) => evt.preventDefault()}
                    onClick={() => insertPlaceholder(p.key)}
                  />
                </Tooltip>
              ))}
            </Box>
          </Paper>

          {FIELD_GROUPS.map((group) => (
            <Box key={group.title} sx={{ mb: 3 }}>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                {group.title}
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {Object.entries(group.fields).map(([key, where]) => {
                  const { label, max, required, multiline } = FIELD_BY_KEY[key];
                  const value = texts[key] ?? "";
                  const empty = !value.trim();
                  const changed = value !== current.defaults[key];
                  return (
                    <Box key={`${email}:${key}`} sx={{ display: "flex", gap: 0.5, alignItems: "flex-start" }}>
                      <TextField
                        label={label}
                        value={value}
                        onChange={(evt) => setText(key, evt.target.value.slice(0, max))}
                        onFocus={() => setFocused(key)}
                        inputRef={(el) => {
                          inputs.current[`${email}:${key}`] = el;
                        }}
                        multiline={multiline}
                        minRows={multiline ? 2 : undefined}
                        fullWidth
                        size="small"
                        helperText={
                          empty
                            ? required
                              ? "Vacío: se usará el texto por defecto."
                              : "Vacío: esta parte no aparecerá en el correo."
                            : `${pqrsdText(where)} · ${value.length}/${max}`
                        }
                      />
                      <Tooltip title={changed ? "Restaurar el texto por defecto" : "Es el texto por defecto"}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!changed}
                            onClick={() => setText(key, current.defaults[key])}
                            aria-label={`Restaurar ${label}`}
                            sx={{ mt: 0.5 }}
                          >
                            <RestartAltIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          ))}
        </Grid>

        {/* Preview: stays in view while scrolling through the fields */}
        <Grid item xs={12} md={6}>
          <Box sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Vista previa
              </Typography>
              {email === "radicado" && (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={radicadoAudience}
                  onChange={(_, next) => next && setRadicadoAudience(next)}
                >
                  {RADICADO_AUDIENCES.map((a) => (
                    <ToggleButton key={a.value} value={a.value} sx={{ textTransform: "none", py: 0.25 }}>
                      {a.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            </Stack>
            {notSentReason && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {notSentReason}
              </Alert>
            )}
            {baseUrl === null && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                No hay una <strong>URL base del portal</strong> válida en Configuración → Ajustes
                generales; el enlace usará la dirección desde la que se anule.
              </Alert>
            )}
            <Paper variant="outlined" sx={{ overflow: "hidden" }}>
              <Box sx={{ px: 2, py: 1, bgcolor: "grey.50", borderBottom: 1, borderColor: "divider" }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Asunto
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
                  {subject}
                </Typography>
              </Box>
              <Box
                component="iframe"
                title="Vista previa del correo de anulación"
                sandbox=""
                srcDoc={html}
                sx={{ display: "block", width: "100%", height: { xs: 560, md: "calc(100vh - 260px)" }, minHeight: 480, border: 0, bgcolor: "#f4f5f7" }}
              />
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
